import { useMutation, useQueryClient } from "@tanstack/react-query";
import { escalateStep } from "../api";
import { wmErrorMessage } from "../api/wm-client";
import { toast } from "../utils/toast";

export interface EscalateStepInput {
  stepUid: string;
  content: string;
  attachmentUids?: string[];
}

/**
 * §Escalate-overdue: raise an overdue step to its escalation contacts.
 *
 * Deliberately NOT a thirteenth mutation on `useStepMutations`: that hook is built around ONE step and is
 * instantiated only by `StepBody`, which holds ids (customerUid, the step's own uid) the queue rows do not
 * have. Here the step uid is a mutation VARIABLE, so a single instance can serve a whole list of rows and
 * `mutation.variables.stepUid` gives per-row busy state without a hook per row.
 */
export const useEscalateStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ stepUid, content, attachmentUids }: EscalateStepInput) =>
      escalateStep(stepUid, content, attachmentUids),
    onSuccess: () => {
      /**
       * "Recorded", never "management notified". Delivery is best-effort and the endpoint cannot confirm
       * it: with no escalation contacts configured on the step, an unprovisioned Ymir template, or an
       * unset topic, the publish is a silent no-op — so "notified" would be a confident lie in exactly
       * the cases where the agent most needs to know it did not land. What IS guaranteed is the record:
       * the step is flagged, the note is on the thread, and the audit row is written.
       */
      toast.success("Escalation recorded");
      // The escalation flips `escalated` on the step detail and adds a comment — refetch the whole
      // nawy-now tree so the chip and the thread appear wherever they are mounted.
      queryClient.invalidateQueries({ queryKey: ["nawy-now"] });
    },
    // The server's copy verbatim — it distinguishes "not overdue yet" from "not yours to escalate",
    // and a generic message would strand the agent on the difference.
    onError: (error: unknown) => toast.error(wmErrorMessage(error)),
  });
};
